/**
 * QBase HTTP 클라이언트 — 세션 쿠키(JSESSIONID) 기반, 응답 표준 해석, 실패는 삼키지 않는다.
 *
 * 응답 표준 (QbaseResponseUtils):
 *   조회  { isSuccess:'Y', resultList:[...] }
 *   저장  { isSuccess:'Y', resultMessage }
 *   오류  { isSuccess:'N', resultMessage }
 * 로그인/세션 (QbaseLoginController): { success:boolean, message, user:{...}, homeSysId, activeSysId }
 */
import { QBASE_API, QBASE_SYS_ID } from '../config/qbase';
import { logError } from './eventLog';
import i18n from '../i18n';
import { notifyUnauthorized } from './sessionExpiry';
import { BE1_API } from '../config/api';
import { errorText } from './errorText';
import { noteResponse as noteMaintenance, noteNetworkError, getMaintenance } from './maintenance';

export class QbaseError extends Error {
  status: number;
  /** 큐베이스 점검(쓰기 동결·DB 전환) 때문에 실패했다 — 로그아웃·재전송 대상이 아니다 (services/maintenance) */
  maintenance: boolean;
  constructor(message: string, status: number, maintenance = false) {
    super(message);
    this.name = 'QbaseError';
    this.status = status;
    this.maintenance = maintenance;
  }
}

/** 점검 때문에 막힌 요청의 오류 — 문구는 점검 안내(입력은 화면에 남아 있다) */
function maintenanceError(status: number): QbaseError {
  const m = getMaintenance();
  const msg = m?.unreachable
    ? i18n.t('common:maintenance.switching')
    : i18n.t('common:maintenance.blocked', { message: m?.message || '-' });
  return new QbaseError(msg, status, true);
}

/** fetch 가 서버에 닿지도 못했다 — DB 전환 재기동 중이면 점검 오류로, 아니면 원래 오류 그대로 */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    if (noteNetworkError()) throw maintenanceError(503);
    throw e;
  }
}

/**
 * 오류에 붙일 경로 — **어떤 주소가 와도 예외를 내지 않는다.**
 *
 * `QBASE_API` 는 같은 오리진일 때 `/qcore/home` 같은 상대 접두사다. 그것을 `new URL(…, base)` 의
 * base 로 주면 브라우저는 base 부터 파싱하다 `Invalid base URL` 로 던진다 — 첫 인자가 절대 주소여도 그렇다.
 */
function pathOf(url: string): string {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname;
  } catch {
    return url || i18n.t('common:error.unknownUrl');
  }
}

async function parse(res: Response): Promise<any> {
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
  // 점검 중(쓰기 동결·DB 전환) — 로그아웃도 '알 수 없는 오류' 기록도 아니다. 띠가 알리고, 저장은 다시 보내지 않는다
  if (noteMaintenance(res, body)) throw maintenanceError(res.status);
  if (!res.ok) {
    const msg = body?.resultMessage || body?.message || `${res.status} ${res.statusText}`;
    // QBase 호출은 우리 백엔드를 거치지 않는다 — 서버 미들웨어가 볼 수 없으므로 여기서 남긴다.
    // 401 은 세션 만료의 정상 흐름이라 제외한다(로그인 화면으로 가는 길이지 사고가 아니다).
    //
    // 기록하다 예외가 나면 **기록이 통째로 사라지고 원래 오류까지 그 예외로 바뀐다.** 실제로 그랬다
    // (2026-09-15: 504 가 화면에 "Invalid base URL" 로 둔갑했고, api.failed 는 한 줄도 안 남았다).
    // 로그인이 끊긴 것은 화면이 알아야 한다 — 알리지 않으면 목록만 비어 보인다 (services/sessionExpiry)
    if (res.status === 401) notifyUnauthorized(pathOf(res.url));
    if (res.status !== 401) {
      try {
        logError('api.failed', new QbaseError(msg, res.status), { where: pathOf(res.url), status: res.status });
      } catch { /* 기록 실패가 원래 오류를 덮지 않는다 */ }
    }
    throw new QbaseError(msg, res.status);
  }
  if (body && body.isSuccess === 'N') {
    throw new QbaseError(body.resultMessage || i18n.t('common:error.requestRejected'), res.status);
  }
  if (body && body.success === false) {
    throw new QbaseError(body.message || i18n.t('common:error.requestRejected'), res.status);
  }
  return body;
}

export async function qbaseGet(path: string, params?: Record<string, string | number | undefined>): Promise<any> {
  const qs = params
    ? '?' + Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  const res = await send(`${QBASE_API}${path}${qs}`, { method: 'GET', credentials: 'include' });
  return parse(res);
}

export async function qbasePost(path: string, body: unknown): Promise<any> {
  const res = await send(`${QBASE_API}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json; charset=UTF-8' },
    body: JSON.stringify(body ?? {}),
  });
  return parse(res);
}

/** 조회 응답의 resultList 만 꺼낸다 (키는 camelCase — 뷰 컬럼 openflow_project_id → openflowProjectId) */
export async function qbaseSelect<T = Record<string, any>>(
  path: string, params?: Record<string, string | number | undefined>,
): Promise<T[]> {
  const body = await qbaseGet(path, params);
  return (body?.resultList ?? []) as T[];
}

// ---------------------------------------------------------------- 인증·세션

export interface QbaseSession {
  user: { loginId: string; usrid: string; name: string; email: string; viewOnly?: boolean | string };
  homeSysId: string;
  activeSysId: string | null;
}

/** 이 브라우저(앱)의 디바이스 식별자 — QBase 편집권은 사용자당 1세션이며 같은 deviceId 의 좀비 세션은 승계된다 */
export const DEVICE_ID = 'openflow-web';

/**
 * 로그인 → 편집권 확보 시도.
 * QBase 는 사용자당 편집 세션을 하나만 허용한다(다른 세션은 조회 전용 → 쓰기 403).
 * 로그인 응답이 viewOnly 면 acquire-edit 를 한 번 시도하고, 그래도 못 얻으면 viewOnly=true 로 돌려준다 —
 * 조용히 성공한 척하지 않는다(저장 시 "조회 전용" 으로 실패한다).
 * @return viewOnly 여부
 */
export async function login(loginId: string, pwd: string): Promise<{ viewOnly: boolean }> {
  const body = await qbasePost('/qbase/api/login', { loginId, pwd, qbaseSysId: QBASE_SYS_ID, deviceId: DEVICE_ID });
  const viewOnly = body?.user?.viewOnly === true || body?.user?.viewOnly === 'Y';
  if (!viewOnly) return { viewOnly: false };
  return { viewOnly: !(await acquireEdit()) };
}

/**
 * 체험 계정으로 바로 로그인 — 비밀번호는 서버(OpenFlow API)에만 있다. 서버가 QBase 에 대신 로그인하고
 * 세션 쿠키를 내려 준다(같은 오리진). 그 뒤는 보통 로그인과 같다 (backend services/trial_login).
 */
export async function trialLogin(loginId: string): Promise<{ viewOnly: boolean }> {
  const res = await fetch(`${BE1_API}/api/trial/login`, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ loginId, qbaseSysId: QBASE_SYS_ID, deviceId: DEVICE_ID }),
  });
  const body = await res.json().catch(() => ({}));
  if (noteMaintenance(res, body)) throw maintenanceError(res.status);
  if (!res.ok || body?.success === false) throw new Error(errorText(body, body?.message || res.statusText));
  const viewOnly = body?.user?.viewOnly === true || body?.user?.viewOnly === 'Y';
  if (!viewOnly) return { viewOnly: false };
  return { viewOnly: !(await acquireEdit()) };
}

/** 로그인 화면의 체험 계정 리본 — 계정 이름만. 꺼져 있거나 서버가 응답하지 않으면 빈 목록 */
export async function trialAccounts(): Promise<string[]> {
  try {
    const res = await fetch(`${BE1_API}/api/trial/accounts`, { credentials: 'include' });
    const body = res.ok ? await res.json() : null;
    return body?.enabled ? (body.accounts || []) : [];
  } catch {
    return [];
  }
}

/** 편집권 획득 시도 — true 면 이 세션이 편집자 */
export async function acquireEdit(): Promise<boolean> {
  const r = await qbasePost('/qbase/api/session/acquire-edit', { deviceId: DEVICE_ID });
  return r?.acquired === true;
}

export async function logout(): Promise<void> {
  try { await qbasePost('/qbase/api/logout', {}); } catch { /* 세션이 이미 없어도 로그아웃은 성공이다 */ }
}

/** 세션 조회 — 401 이면 null (미로그인은 오류가 아니라 상태다) */
export async function getSession(): Promise<QbaseSession | null> {
  const res = await send(`${QBASE_API}/qbase/api/session`, { credentials: 'include' });
  if (res.status === 401) return null;
  const body = await parse(res);
  return { user: body.user, homeSysId: body.homeSysId, activeSysId: body.activeSysId ?? null };
}

/**
 * 작업 시스템 확정 — 오픈플로우는 시스템 선택 화면이 없다.
 * QBase 의 읽기 스코프·쓰기 귀속은 activeSysId 축 위에 있는데 로그인만으로는 채워지지 않으므로
 * 계정 소속(homeSysId)을 그대로 작업 시스템으로 선택한다 (오픈데이터모델러 AppInitializer 선례).
 */
export async function ensureActiveSystem(session: QbaseSession): Promise<string | null> {
  const homeSysId = session.homeSysId;
  if (!homeSysId) return null;
  if (session.activeSysId === homeSysId) return homeSysId;
  await qbasePost('/qbase/api/session/select-system', { qbaseSysId: homeSysId });
  return homeSysId;
}

export async function nextId(dmnNm: string): Promise<string> {
  const body = await qbasePost('/qbase/nextid', { qbaseSysId: QBASE_SYS_ID, dmnNm });
  if (!body?.nextId) throw new QbaseError(i18n.t('common:error.idFailed', { name: dmnNm }), 500);
  return body.nextId as string;
}
